<script setup>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";

const props = defineProps({
  users: {
    type: Array,
    default: () => [],
  },
  showDeleted: {
    type: Boolean,
    default: false,
  },
  currentUserId: {
    type: Number,
    default: null,
  },
  isSuperAdmin: {
    type: Boolean,
    default: false,
  },
  canChangeUserRole: {
  type: Boolean,
  default: false,
  },
});

const emit = defineEmits([
  "show-user",
  "toggle-block",
  "open-user-page",
  "delete-user",
  "restore-user",
  "change-role",
]);

const toast = useToast();

function getUserRoleLabel(user) {
  return user?.roles?.[0] || "—";
}

function getClientTypeLabel(user) {
  return user?.clientType || "—";
}

function isSuperAdminUser(user) {
  return user?.roles?.includes("God") || user?.email === "admin@offercraft.com";
}

function canManageUser(user) {
  if (isSuperAdminUser(user)) {
    return false;
  }

  if (!props.isSuperAdmin && user?.roles?.includes("Admin")) {
    return false;
  }

  return true;
}

function isCurrentUser(user) {
  return props.currentUserId === user.id;
}

function confirmDeleteUser(user) {
  if (isSuperAdminUser(user)) {
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: "Нельзя удалить главного администратора",
      life: 3000,
    });
    return;
  }

  if (!canManageUser(user)) {
    toast.add({
      severity: "warn",
      summary: "Доступ запрещен",
      detail: "У вас нет прав на удаление этого пользователя",
      life: 3000,
    });
    return;
  }

  const isConfirmed = confirm(
    `Вы уверены, что хотите удалить пользователя "${
      user.email || user.fullName || user.id
    }"?`,
  );
  if (isConfirmed) {
    emit("delete-user", user);
  }
}

function confirmRestoreUser(user) {
  const isConfirmed = confirm(
    `Восстановить пользователя "${user.email || user.fullName || user.id}"?`,
  );
  if (isConfirmed) {
    emit("restore-user", user);
  }
}

function handleToggleBlock(user) {
  if (isSuperAdminUser(user)) {
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: "Нельзя заблокировать главного администратора",
      life: 3000,
    });
    return;
  }

  if (!canManageUser(user)) {
    toast.add({
      severity: "warn",
      summary: "Доступ запрещен",
      detail: "У вас нет прав на изменение статуса этого пользователя",
      life: 3000,
    });
    return;
  }

  emit("toggle-block", user);
}

function handleShowUser(user) {
  if (isSuperAdminUser(user) && !props.isSuperAdmin) {
    toast.add({
      severity: "warn",
      summary: "Доступ запрещен",
      detail: "У вас нет прав на просмотр деталей этого пользователя",
      life: 3000,
    });
    return;
  }

  emit("show-user", user);
}
</script>

<template>
  <div class="users-table-wrap">
    <DataTable
      :value="props.users"
      class="users-table"
      responsiveLayout="scroll"
      stripedRows
      :paginator="true"
      :rows="40"
      :rowsPerPageOptions="[20, 40, 80]"
    >
      <Column field="id" header="ID" style="width: 90px" />
      <Column field="email" header="Email" />
      
      <Column header="Роль" style="min-width: 190px">
        <template #body="slotProps">
          <div class="role-cell">
            <span
              class="role-chip"
              :class="{ 'role-chip--super': isSuperAdminUser(slotProps.data) }"
            >
              {{ getUserRoleLabel(slotProps.data) }}
            </span>

            <Button
              v-if="
                props.canChangeUserRole &&
                !slotProps.data.isDeleted &&
                !isCurrentUser(slotProps.data) &&
                !isSuperAdminUser(slotProps.data)
              "
              label="Изменить"
              icon="pi pi-pencil"
              class="change-role-btn"
              size="small"
              outlined
              @click="$emit('change-role', slotProps.data)"
            />
          </div>
        </template>
      </Column>

      <Column header="Тип клиента">
        <template #body="slotProps">
          {{ getClientTypeLabel(slotProps.data) }}
        </template>
      </Column>

      <Column header="Статус">
        <template #body="slotProps">
          <div style="display: flex; flex-direction: column; gap: 4px">
            <span
              class="status-chip"
              :class="{ 'status-chip--blocked': !slotProps.data.isActive }"
            >
              {{ slotProps.data.isActive ? "Активен" : "Заблокирован" }}
            </span>
            <span
              v-if="slotProps.data.isDeleted"
              class="status-chip status-chip--deleted"
            >
              Удален
              {{
                slotProps.data.deletedAt
                  ? new Date(slotProps.data.deletedAt).toLocaleDateString()
                  : ""
              }}
            </span>
          </div>
        </template>
      </Column>

      <Column header="Действия" style="min-width: 420px">
        <template #body="slotProps">
          <div class="actions">
            <Button
              v-if="
                props.isSuperAdmin ||
                (!isSuperAdminUser(slotProps.data) &&
                  canManageUser(slotProps.data))
              "
              label="Подробнее"
              icon="pi pi-eye"
              class="details-btn"
              outlined
              @click="handleShowUser(slotProps.data)"
            />

            <Button
              v-if="
                !slotProps.data.isDeleted &&
                !isCurrentUser(slotProps.data) &&
                canManageUser(slotProps.data)
              "
              :label="
                slotProps.data.isActive ? 'Заблокировать' : 'Разблокировать'
              "
              :icon="slotProps.data.isActive ? 'pi pi-lock' : 'pi pi-lock-open'"
              class="block-btn"
              outlined
              @click="handleToggleBlock(slotProps.data)"
            />

            <Button
              v-if="
                !slotProps.data.isDeleted &&
                props.isSuperAdmin &&
                !isCurrentUser(slotProps.data) &&
                canManageUser(slotProps.data)
              "
              label="Удалить"
              icon="pi pi-trash"
              class="delete-btn"
              outlined
              severity="danger"
              @click="confirmDeleteUser(slotProps.data)"
            />

            <!-- Кнопка восстановления - только для суперадмина -->
            <Button
              v-if="
                slotProps.data.isDeleted &&
                props.isSuperAdmin &&
                !isCurrentUser(slotProps.data)
              "
              label="Восстановить"
              icon="pi pi-refresh"
              class="restore-btn"
              outlined
              @click="confirmRestoreUser(slotProps.data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
.users-table-wrap {
  padding: 0 24px 24px;
}

.actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.role-chip,
.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.role-chip {
  background: #f6f6f6;
  border: 1px solid #ececec;
  color: #4d4d4d;
}

.role-chip--super {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
}

.status-chip {
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: #16a34a;
}

.status-chip--blocked {
  background: #f4f4f4;
  border-color: #e4e4e4;
  color: #6f6f6f;
}

.status-chip--deleted {
  background: rgba(220, 38, 38, 0.1);
  border-color: #dc2626;
  color: #dc2626;
}

:deep(.users-table .p-datatable-table) {
  min-width: 860px;
}

:deep(.users-table .p-datatable-thead > tr > th) {
  background: #ffffff;
  color: #6b6b6b;
  font-size: 13px;
  font-weight: 700;
  border-color: #efefef;
  padding: 16px 14px;
}

:deep(.users-table .p-datatable-tbody > tr > td) {
  padding: 16px 14px;
  border-color: #f0f0f0;
  color: #111111;
  vertical-align: middle;
}

:deep(.users-table .p-datatable-tbody > tr:hover) {
  background: #fcfcfc;
}

:deep(.details-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d9d9d9;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.details-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.block-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.block-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.page-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d9d9d9;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.page-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.delete-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dc2626;
  color: #dc2626;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.delete-btn.p-button:hover) {
  background: #fee;
  border-color: #dc2626;
  color: #dc2626;
}

:deep(.restore-btn.p-button) {
  background: #ffffff;
  border: 1px solid #10b981;
  color: #10b981;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.restore-btn.p-button:hover) {
  background: #10b981;
  border-color: #10b981;
  color: #ffffff;
}

@media (max-width: 760px) {
  .users-table-wrap {
    padding: 0 12px 16px;
    overflow-x: auto;
  }

  .actions {
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .users-table-wrap {
    padding: 0 8px 12px;
  }

  .actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

.role-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

:deep(.change-role-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d9d9d9;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.change-role-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}
</style>
